import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Scale } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";

/**
 * Terms of Service — /terms
 * Follows the same layout convention as PrivacyPolicyPage.jsx — same hero band,
 * same numbered-section pattern, same platinum body typography.
 *
 * Content resolutions (per build brief):
 *   • Entity wording — mirrors PrivacyPolicyPage.jsx: "a brand operated by
 *     Esthera Ventures Global, based in India". Update both files together.
 *   • Governing law — kept verbatim as Maharashtra / Pune per the brief.
 */

const LAST_UPDATED = "November 2026";

const openingLine =
  'EstheraRealityOS is a brand operated by Esthera Ventures Global, ' +
  'based in India. By using this Site, you agree to these Terms of Service. ' +
  'If you do not agree, please do not use the Site.';

function Section({ n, title, children }) {
  return (
    <section className="mt-12 fade-up">
      <div className="flex items-baseline gap-3">
        <span className="text-[11px] tracking-[0.32em] uppercase text-sapphire-glow/85 font-semibold">
          {String(n).padStart(2, "0")}
        </span>
        <h2 className="font-display text-platinum text-[22px] sm:text-[26px] leading-[1.2] tracking-[-0.012em]">
          {title}
        </h2>
      </div>
      <div className="mt-4 text-[14.5px] text-platinum/72 leading-[1.75] space-y-4 max-w-[680px]">
        {children}
      </div>
    </section>
  );
}

export default function TermsPage() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="terms-page">
      <AmbientParticles />
      <Navbar />

      <section className="relative pt-32 pb-10">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="fade-up inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10.5px] tracking-[0.24em] uppercase text-sapphire-glow font-semibold"
            style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(96,165,250,0.28)" }}>
            <Scale className="w-3 h-3" />
            Legal
          </div>
          <h1 className="mt-5 font-display text-platinum text-[36px] sm:text-[44px] lg:text-[54px] leading-[1.05] tracking-[-0.02em] fade-up"
            style={{ animationDelay: "60ms" }}>
            Terms of <span className="text-sapphire-glow" style={{ textShadow: "0 0 30px rgba(96,165,250,0.35)" }}>Service</span>
          </h1>
          <p className="mt-3 text-[12.5px] tracking-[0.22em] uppercase text-platinum/45 fade-up" style={{ animationDelay: "120ms" }}>
            Last updated · {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="rounded-2xl border border-white/[0.06] p-6 sm:p-10 lg:p-14 fade-up"
            style={{ background: "linear-gradient(180deg, rgba(15,20,36,0.55) 0%, rgba(10,15,26,0.85) 100%)" }}>

            <h2 className="font-display text-platinum text-[22px] sm:text-[26px] leading-[1.2] tracking-[-0.012em]">
              {"Welcome to "}
              <span className="text-sapphire-glow" style={{ textShadow: "0 0 22px rgba(96,165,250,0.35)" }}>
                estherarealityos.com
              </span>
              .
            </h2>
            <p className="mt-4 text-[14.5px] text-platinum/78 leading-[1.75] max-w-[680px]">
              {openingLine}
            </p>

            <Section n={1} title="What Esthera Does">
              <p>
                EstheraRealityOS provides an AI-powered operations audit, and related information,
                insight, and (where engaged) automation services for real estate brokerages, developers,
                and related businesses. Requesting an Operations Audit through this Site does not, by
                itself, create any paid engagement — it is a diagnostic offered at Esthera&rsquo;s discretion.
              </p>
              <p>
                Any paid services (audits leading to a proposal, builds, or ongoing retainers) are governed
                by a separate, signed Service Agreement between Esthera and the client — not by these
                website Terms.
              </p>
            </Section>

            <Section n={2} title="Use of the Site">
              <p>You agree to use this Site only for lawful purposes. You agree not to:</p>
              <ul className="list-disc pl-5 space-y-2 marker:text-sapphire-glow/60">
                <li>Submit false or misleading information through our forms</li>
                <li>Attempt to access, scrape, or extract data from the Site beyond normal browsing use</li>
                <li>Use the Site to transmit anything harmful, unlawful, or infringing</li>
                <li>Interfere with the Site&rsquo;s normal operation</li>
              </ul>
            </Section>

            <Section n={3} title="No Guarantee of Results">
              <p>
                Any insights, findings, or recommendations provided through the Operations Audit or
                elsewhere on this Site are based on the information you provide and general industry
                patterns. They are provided for informational purposes and do not constitute a guarantee
                of any specific business outcome, revenue result, or performance improvement. Actual
                results from any engagement depend on factors outside Esthera&rsquo;s control.
              </p>
            </Section>

            <Section n={4} title="Intellectual Property">
              <p>
                All content on this Site — including text, design, graphics, the Esthera name, logo, and
                branding — is the property of Esthera Ventures Global or its licensors, and is protected
                by applicable intellectual property law. You may not reproduce, distribute, or create
                derivative works from this content without our written permission.
              </p>
              <p>
                Any audit report, proposal, or other deliverable you receive from Esthera is licensed to
                you for your own internal business use; it remains Esthera&rsquo;s intellectual property
                unless otherwise agreed in a signed Service Agreement.
              </p>
            </Section>

            <Section n={5} title="Third-Party Links and Tools">
              <p>
                This Site may link to or use third-party tools (such as scheduling or form services).
                We are not responsible for the content, policies, or practices of third-party sites or services.
              </p>
            </Section>

            <Section n={6} title="Disclaimer of Warranties">
              <p>
                This Site and its content are provided &ldquo;as is,&rdquo; without warranties of any kind, whether
                express or implied. We do not warrant that the Site will be uninterrupted, error-free, or
                completely secure.
              </p>
            </Section>

            <Section n={7} title="Limitation of Liability">
              <p>
                To the fullest extent permitted by law, Esthera Ventures Global will not be liable for
                any indirect, incidental, or consequential damages arising from your use of this Site.
                This limitation does not apply to any separately signed Service Agreement, which has its
                own liability terms.
              </p>
            </Section>

            <Section n={8} title="Governing Law">
              <p>
                These Terms are governed by the laws of Maharashtra, India, and any disputes will be
                resolved in the courts of Pune, India.
              </p>
            </Section>

            <Section n={9} title="Changes to These Terms">
              <p>
                We may update these Terms from time to time. The &ldquo;Last Updated&rdquo; date at the top reflects
                the most recent version. Continued use of the Site after changes means you accept the
                updated Terms.
              </p>
            </Section>

            <Section n={10} title="Contact Us">
              <p>Questions about these Terms? Contact us at:</p>
              <p>
                <a href="mailto:legal@estherarealityos.com" className="text-sapphire-glow hover:text-white transition-colors">
                  legal@estherarealityos.com
                </a>
              </p>
            </Section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
